import { createContext, useContext, useEffect, useState } from "react";
import { AuthApi, getToken, setToken, clearToken } from "../api";

type AuthState =
  | { status: "loading" }
  | { status: "loggedOut" }
  | { status: "loggedIn"; userId: string };

const authContext = createContext<{
  auth: AuthState;
  login: (input: {
    username: string;
    password: string;
  }) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
}>({
  auth: { status: "loading" },
  login: async () => ({ success: false, error: "not ready" }),
  logout: () => {},
});

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [auth, setAuth] = useState<AuthState>({ status: "loading" });

  useEffect(() => {
    const attemptRestore = async () => {
      const token = getToken();
      if (!token) {
        setAuth({ status: "loggedOut" });
        return;
      }
      const result = await AuthApi.getUser(token);
      if (result?.success && result?.data?.id) {
        setAuth({ status: "loggedIn", userId: result.data.id });
      } else {
        clearToken();
        setAuth({ status: "loggedOut" });
      }
    };
    attemptRestore();
  }, []);

  const login = async (input: { username: string; password: string }) => {
    const result = await AuthApi.login(input);
    if (!result?.success || !result?.token) {
      return { success: false, error: result?.message || "Login failed" };
    }
    setToken(result.token);
    const user = await AuthApi.getUser(result.token);
    if (!user?.success || !user?.data?.id) {
      return { success: false, error: "Could not load user" };
    }
    setAuth({ status: "loggedIn", userId: user.data.id });
    return { success: true };
  };

  const logout = () => {
    clearToken();
    setAuth({ status: "loggedOut" });
  };

  return (
    <authContext.Provider value={{ auth, login, logout }}>
      {children}
    </authContext.Provider>
  );
};

export const useAuth = () => useContext(authContext);
